import { WIX_BLOG } from '@wix/app-definition-ids';
import { getAppDefinitions } from '@wix/members-area-app-definitions/get-app-definitions';
import type {
  IntegrationApplication,
  IntegrationApplicationMap,
  MembersAreaAppsOptions,
} from '@wix/members-area-integration-kit';
import type { EditorSDK } from '@wix/platform-editor-sdk';
import { createInstance } from 'i18next';

import { ProfileType } from '../../types/blog-writer';
import { createBIService } from '../../utils/bi';
import { shouldEnableExtendedUninstallMASubApps } from '../../utils/experiments';
import { log, toMonitored } from '../../utils/monitoring';
import { getUniqueApplications } from '../../utils/pages';
import { asyncFilter } from '../../utils/promises';
import { isApplicationReady } from '../applicationState';
import {
  ALWAYS_REGISTERED_APPS,
  ALWAYS_REGISTERED_SOCIAL_MA_APPS,
  APP_TOKEN,
  MY_ACCOUNT_APP_DEF_ID,
} from '../constants';
import { removeLoosePagesOutsideMembersArea } from '../data-fixers';
import enforceSequentiality from '../enforceSequentiality';
import { addApplications } from '../platform-api/addApplications';
import { removeMembersAreaPage } from '../platform-api/removeMembersAreaPage';
import * as menusWrapper from '../wrappers/menus';
import * as state from './applicationState';
import { getIsUninstalling, setIntegratedApps } from './applicationState';
import { mergeGlobalSettings } from './members-area';
import { isMyWishlist } from './myWishlistIntegration';
import { getPageByIntegrationApp, hasSocialPages } from './pages';
import { getProfileType, hasOnlyBlogProfile, resetRolesOnSocialVertical, setProfileType } from './profile';
import * as routersService from './routers';
import { uninstallDeletableApps } from './uninstall-apps';

const isAddApplication = (app: IntegrationApplication) => app.method === 'addApplication';
const hasNoDependencies = (allApps: IntegrationApplication[]) => (app: IntegrationApplication) =>
  !allApps.some((app2) => app2.pageId === app.pageId);

export const isAppInstalled = (editorSDK: EditorSDK) => {
  return (appDefinitionId: string) => editorSDK.application.isApplicationInstalled(APP_TOKEN, { appDefinitionId });
};

export const maybeAddApplications = async (
  applications: IntegrationApplication[],
  editorSDK: EditorSDK,
  options?: { biData?: { origin?: string } },
) => {
  const isReady = await isApplicationReady(editorSDK, { shouldLog: false });
  if (!isReady) {
    console.warn('Members Area installation was corrupted so the integrations pages will not be added');
    log('Skipping addApplications as the application is not ready and probably already deleted');
    return;
  }
  const forceHorizontalLayout = false;

  return toMonitored('editorApi.addApplications', () =>
    addApplications({ editorSDK, applications, forceHorizontalLayout, biData: options?.biData }),
  );
};

export const registerMembersAreaApps = async (
  applications: IntegrationApplication[],
  verticalAppDefId: string,
  editorSDK: EditorSDK,
  applicationsOptions: MembersAreaAppsOptions,
) => {
  return enforceSequentiality('editorApi.registerMembersAreaApps', () =>
    toMonitored('editorApi.registerMembersAreaApps', async () => {
      const applicationDefinitions = await getAppDefinitions({
        applications,
        editorSDK,
        applicationsOptions,
        // @ts-expect-error - versions mismatch
        i18next: createInstance(),
      });
      const currentIntegratedAppsMap = state.getAllIntegratedApps();
      const verticalsApps = getUniqueApplications([
        ...(currentIntegratedAppsMap[verticalAppDefId] ?? []),
        ...applicationDefinitions,
      ]);

      state.setIntegratedApps({
        ...currentIntegratedAppsMap,
        [verticalAppDefId]: verticalsApps,
      });

      try {
        await resetRolesOnSocialVertical(editorSDK, verticalAppDefId, verticalsApps);
      } catch (e) {
        log('resetRolesOnSocialVertical failed in registerMembersAreaApps', {
          extra: { error: e },
        });
      }

      removeLoosePagesOutsideMembersArea(editorSDK, applications);
    }),
  );
};

export const installRegisteredApps = async (
  verticalAppDefId: string,
  editorSDK: EditorSDK,
  options?: { biData?: { origin?: string } },
) => {
  const biService = await createBIService({ editorSDK });
  biService.verticalTriggeredMaInstallInitiated({ originAppId: verticalAppDefId });

  const integrationApps = state.getVerticalsApps(verticalAppDefId) as IntegrationApplication[];
  const integrationAppsToInstall = integrationApps.filter((app) => app.shouldInstallInitially !== false);

  if (integrationAppsToInstall.length > 0) {
    await maybeAddApplications(integrationAppsToInstall, editorSDK, options);
  }

  await maybeSetBlogWriterProfileType(editorSDK, verticalAppDefId);

  biService.verticalTriggeredMaInstallSuccess({ originAppId: verticalAppDefId });
};

const maybeSetBlogWriterProfileType = async (editorSDK: EditorSDK, verticalAppDefId: string) => {
  try {
    if (verticalAppDefId === WIX_BLOG && (await hasOnlyBlogProfile(editorSDK))) {
      const profileType = state.getIsBlogWriterProfilesOnly() ? ProfileType.BWP_ONLY : ProfileType.BWP_ALL;
      const currentProfileType = await getProfileType(editorSDK);
      if (profileType !== currentProfileType) {
        await setProfileType(editorSDK, profileType);
      }
    }
  } catch (e) {
    log('Failed to set Blog Profile type', { extra: { error: e } });
  }
};

export const handleVerticalDeletion = async (verticalAppDefId: string, editorSDK: EditorSDK) => {
  const verticalsApps = state.getVerticalsApps(verticalAppDefId) as IntegrationApplication[];
  const allIntegratedAppsMap: IntegrationApplicationMap = state.getAllIntegratedApps();
  const installedVerticalIds: string[] = await asyncFilter(
    Object.keys(allIntegratedAppsMap),
    isAppInstalled(editorSDK),
  );
  const appsOfOtherVerticals = installedVerticalIds.reduce<IntegrationApplication[]>(
    (acc, appDefId) => acc.concat((allIntegratedAppsMap[appDefId] as IntegrationApplication[]) ?? []),
    [],
  );
  const integratedAppsWithNoDependencies = verticalsApps.filter(hasNoDependencies(appsOfOtherVerticals));
  // TPA Section deletion is handled in "pageDeleted" event
  const integratedAppsToDelete = integratedAppsWithNoDependencies.filter(isAddApplication);

  for (const app of integratedAppsToDelete) {
    const pageToRemove = await getPageByIntegrationApp({ editorSDK, app });
    if (pageToRemove) {
      await toMonitored('editorApi.removeMembersAreaPage', () =>
        removeMembersAreaPage({ editorSDK, id: pageToRemove.id! }),
      );
    }
  }

  if (await shouldEnableExtendedUninstallMASubApps()) {
    await uninstallDeletableApps(editorSDK, integratedAppsWithNoDependencies);
  }
};

export const registerAlwaysAvailableApps = async (editorSDK: EditorSDK) => {
  const isSocialMA = await hasSocialPages(editorSDK);
  const santaMembersApps = await getAppDefinitions({
    applications: ALWAYS_REGISTERED_APPS.concat(isSocialMA ? ALWAYS_REGISTERED_SOCIAL_MA_APPS : []),
    editorSDK,
    // @ts-expect-error - versions mismatch
    i18next: createInstance(),
  });

  setIntegratedApps({
    ...state.getAllIntegratedApps(),
    [MY_ACCOUNT_APP_DEF_ID]: santaMembersApps,
  });
};

export const getRegisteredApps = async (editorSDK: EditorSDK) => {
  return enforceSequentiality('editorApi.getRegisteredApps', () =>
    toMonitored('editorApi.getRegisteredApps', async () => {
      const allIntegratedAppsMap = state.getAllIntegratedApps();
      const registeredVerticalIds = Object.keys(allIntegratedAppsMap);
      const installedVerticalIds: string[] = await asyncFilter(registeredVerticalIds, isAppInstalled(editorSDK));

      return installedVerticalIds.reduce<Promise<IntegrationApplicationMap>>(
        async (acc, appDefId) => ({
          ...(await acc),
          [appDefId]: await asyncFilter(
            allIntegratedAppsMap[appDefId],
            async (app: IntegrationApplication) =>
              !(await getPageByIntegrationApp({ editorSDK, app })) && !isMyWishlist(app),
          ),
        }),
        Promise.resolve({}),
      );
    }),
  );
};

export const getOnlyInstalledVerticals = async (editorSDK: EditorSDK) => {
  const allVerticals = state.getAllIntegratedApps();
  const installedVerticalIds: string[] = await asyncFilter(Object.keys(allVerticals), await isAppInstalled(editorSDK));

  return installedVerticalIds.reduce<IntegrationApplicationMap>(
    (verticals, verticalId) => ({
      ...verticals,
      [verticalId]: allVerticals[verticalId],
    }),
    {},
  );
};

export const handleVerticalSectionDeletion = async (editorSDK: EditorSDK, pageRole: string) => {
  // No need to handle if MA is being deleted as a whole
  if (getIsUninstalling()) {
    return;
  }

  const routers = await routersService.getMembersAreaRouters(editorSDK);
  const { patternKey, router } = routersService.getPatternByPageRole({ routers, pageRole });

  if (!patternKey) {
    return;
  }

  if (router) {
    try {
      await routersService.deleteRouterPatternByPatternKey({ editorSDK, router, patternKey });
    } catch (e) {
      log('Failed to remove pattern from router when handling vertical section deletion', {
        extra: { patternKey, router, stack: (e as Error)?.stack },
      });
    }
  }

  try {
    const pattern = patternKey.replace(/\/?(.*)/, '$1');
    await menusWrapper.removePatternFromAllMenus({ editorSDK, pattern });
  } catch (e) {
    log('Failed to remove pattern from menus when handling vertical section deletion', {
      extra: { patternKey, router, stack: (e as Error)?.stack },
    });
  }
};

export const setFollowingFollowersSetting = (editorSDK: EditorSDK, value: boolean) => {
  try {
    return mergeGlobalSettings(editorSDK, { showFollowers: value });
  } catch (e) {
    log('Failed to set showFollowers setting to false for blog writers profile integration', {
      extra: { error: (e as Error).toString() },
    });
  }
};
